import React, { useState } from 'react';
import { Send } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    service: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const response = await axios.post(`${BACKEND_URL}/api/contact`, formData);
      
      toast.success(response.data.message || "Message Sent!", {
        description: "Thank you for contacting us. We'll get back to you within 24 hours.",
        duration: 5000,
      });

      // Reset form
      setFormData({
        name: '',
        email: '',
        company: '',
        phone: '',
        service: '',
        message: ''
      });
    } catch (error) {
      console.error('Form submission error:', error);
      toast.error("Failed to send message", {
        description: error.response?.data?.detail || "Please try again or contact us directly at shaista.nehan@gentechstaffing.com",
        duration: 5000,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="dark-page">
      <section className="page-hero">
        <div className="hero-image-wrapper">
          <img 
            src="https://images.unsplash.com/photo-1535957998253-26ae1ef29506" 
            alt="Modern workspace with natural light"
            className="hero-background-image"
          />
          <div className="hero-overlay"></div>
        </div>
        <h1 className="display-large">Let's Talk About Your Needs</h1>
        <p className="body-large page-subtitle">
          Whether you need IT talent, AI implementation, or strategic guidance—we're here to help you move forward.
        </p>
      </section>

      <section className="contact-section">
        <div className="contact-content">
          <div className="contact-info">
            <h2 className="display-medium">Connect With Us</h2>
            <p className="body-large">
              Ready to accelerate your digital transformation? Fill out the form and we'll respond within 24 hours—or call us directly for immediate assistance.
            </p>
            
            <div className="contact-details">
              <div className="contact-detail-item">
                <h3 className="heading-3">Email</h3>
                <p className="body-medium">contact@gentechstaffing.com</p>
              </div>
              <div className="contact-detail-item">
                <h3 className="heading-3">Phone</h3>
                <p className="body-medium">(289) 633-4269</p>
              </div>
              <div className="contact-detail-item">
                <h3 className="heading-3">Canadian Offices</h3>
                <div className="locations-list">
                  <p className="body-medium">Toronto • Vancouver • Calgary • Montreal</p>
                  <p className="body-medium">Ottawa • Edmonton • Kitchener-Waterloo</p>
                  <p className="body-medium">Québec • Saskatoon</p>
                </div>
              </div>
              <div className="contact-detail-item">
                <h3 className="heading-3">Business Hours</h3>
                <p className="body-medium">Monday - Friday: 9:00 AM - 6:00 PM EST</p>
              </div>
            </div>
          </div>

          <form className="contact-form" onSubmit={handleSubmit}>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="name" className="form-label">Full Name *</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="form-input"
                />
              </div>
              <div className="form-group">
                <label htmlFor="email" className="form-label">Email *</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="form-input"
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="company" className="form-label">Company</label>
                <input
                  type="text"
                  id="company"
                  name="company"
                  value={formData.company}
                  onChange={handleChange}
                  className="form-input"
                />
              </div>
              <div className="form-group">
                <label htmlFor="phone" className="form-label">Phone</label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className="form-input"
                />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="service" className="form-label">Service Interested In *</label>
              <select
                id="service"
                name="service"
                value={formData.service}
                onChange={handleChange}
                required
                className="form-select"
              >
                <option value="">Select a service</option>
                <optgroup label="Technology & Consulting">
                  <option value="rpa">Robotic Process Automation</option>
                  <option value="erp-crm">ERP / CRM Solutions</option>
                  <option value="app-dev">Application Development</option>
                  <option value="staffing">IT Staffing</option>
                  <option value="ai-strategy">AI Strategy & Readiness</option>
                  <option value="gen-ai-llm">Generative AI / LLM</option>
                  <option value="data-ml-ops">Data Engineering & ML Ops</option>
                  <option value="ai-governance">AI Governance</option>
                  <option value="industry-ai">Industry-Specific AI</option>
                  <option value="ai-automation">AI-Powered Automation</option>
                  <option value="consulting">IT Consulting</option>
                </optgroup>
                <optgroup label="Workforce Solutions">
                  <option value="eor">Employer of Record (EOR)</option>
                  <option value="aor">Agent of Record (AOR)</option>
                  <option value="direct-sourcing">Direct Sourcing</option>
                  <option value="global-talent">Global Talent Solutions</option>
                </optgroup>
                <option value="other">Other</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="message" className="form-label">Message *</label>
              <textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                required
                rows="6"
                className="form-textarea"
              />
            </div>

            <button type="submit" className="btn-primary btn-large" disabled={isSubmitting}>
              {isSubmitting ? 'Sending...' : 'Send Message'}
              <Send size={20} />
            </button>
          </form>
        </div>
      </section>
    </div>
  );
};

export default Contact;